import AssistantStore from "@/helpers/assistant-store.js";
import CMS from "@/helpers/cms/CMS.js";
import CmsPage from "@/helpers/cms/CmsPage.js";
import CmsWebsite from "@/helpers/cms/CmsWebsite.js";
import router from "@/router.js";
import Vue from 'vue';
import swal from "sweetalert";
import Vuex from 'vuex';
import {getLocale, normalizeLocaleCode, getFallbackLocale, setFallbackLocale, setLocale, tr} from "@vue-components/helpers/I18nPluginHelper.js";
import Network from "@vue-components/helpers/Network.js";
import NetworkV2 from "@vue-components/helpers/NetworkV2.js";
import GoogleFontStore from "@/helpers/google-font-store.js";
import MapStore from "@/helpers/maps/map-store.js";
import ClipboardStore from "@/helpers/clipboard/clipboard-store.js";
import TextCompareStore from "@/helpers/text-compare-store.js";
import moment from "moment";

Vue.use(Vuex);

// TODO: move to utils.js?
const normalizeLanguageCodes = (languages = []) => {
    if (!Array.isArray(languages)) return [];
    const normalized = [];
    const seen = new Set();
    for (const lang of languages) {
        const code = normalizeLocaleCode(lang);
        if (!code) continue;
        const key = code.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        normalized.push(code);
    }
    return normalized;
};

// Same detection as App.vue, readable before App.vue mounted() has run (loadWebsite can be dispatched first)
const isKioskRequest = (state) => {
    return state.isKioskDevice ||
        new URLSearchParams(window.location.search).get(`display`) === `kiosk` ||
        localStorage.getItem(`kiosk`) === `yes`;
};

/*
 * Kiosk offline copy of the website payload, owned by the app instead of relying only on the
 * service worker runtime cache: that single entry is rewritten on every boot, and Chromium removes
 * the previous entry before writing the new one, so a player restart/power cut during the write
 * leaves nothing to boot from offline. Two rotating copies make that window harmless.
 */
const KIOSK_WEBSITE_CACHE = `kiosk-website`;
const KIOSK_WEBSITE_KEYS = [`/__kiosk-website/latest`, `/__kiosk-website/backup`];

const saveKioskWebsite = async(serializedWebsite) => {
    if (!window.caches) {
        return;
    }
    try {
        const cache = await caches.open(KIOSK_WEBSITE_CACHE);
        for (const key of KIOSK_WEBSITE_KEYS) {
            await cache.put(key, new Response(serializedWebsite, {headers: {"Content-Type": `application/json`}}));
        }
    } catch (e) {
        console.warn(`Could not save the kiosk offline copy of the website`, e);
    }
};

const loadKioskWebsiteCopy = async() => {
    if (!window.caches) {
        return null;
    }
    try {
        const cache = await caches.open(KIOSK_WEBSITE_CACHE);
        for (const key of KIOSK_WEBSITE_KEYS) {
            const response = await cache.match(key);
            if (response) {
                return await response.json();
            }
        }
    } catch (e) {
        console.warn(`Could not read the kiosk offline copy of the website`, e);
    }
    return null;
};

// Diagnostic for offline boot failures: tells whether the service worker cache had the payload
const logKioskCacheState = async(url) => {
    if (!window.caches) {
        return;
    }
    try {
        const keys = (await (await caches.open(`everything`)).keys()).map(k => k.url);
        const estimate = navigator.storage?.estimate ? await navigator.storage.estimate() : null;
        console.warn(`Kiosk website request failed. Service worker cache has the URL: ${keys.some(k => k.endsWith(url))}, ` +
            `entries: ${keys.length}, storage: ${estimate ? `${Math.round(estimate.usage / 1048576)}/${Math.round(estimate.quota / 1048576)} MB` : `unknown`}`);
    } catch (e) {
        console.warn(`Could not inspect kiosk caches`, e);
    }
};

export default new Vuex.Store({
    state: {
        applicationLoading: false,
        applicationReady: false,
        dealerid: null,
        page: null,
        cmsItemInfoId: null,
        pages: {},
        website: null,
        cache: `no-cache`, // for editor advanced purpose
        websiteStyleVersion: 0,
        cms: null,
        user: null,
        activeTutorial: null,
        env: localStorage.getItem(`env`) || `dev`,
        sessionData: {
            excludeAnalytics: false,
            medium: null,
            source: null,
            campaign: null,
            utms: {}, // Google Standard UTM parameters
        },
        editMode: true,
        previewLanguage: null,
        // mobile | tablet | kiosk | desktop
        previewMode: `desktop`,
        displayMode: `desktop`,
        languageSelectModal: false,
        feedbackModal: {
            visible: false,
            active: false,
            data: null
        },
        previewDeviceFrame: true,
        editRawHtml: false,
        blockHtmlEditorEnabled: {}, // Track which blocks have HTML editor enabled: { blockId: true/false }
        zoom: 100,
        accessibility: {
            wheelchair: JSON.parse(localStorage.getItem(`wheelchair`)) || false
        },
        display: {
            review: false,
            comparison: false,
        },
        // Kiosk Idle Screen. Configured in website settings, but can be configured through player params (legacy)
        idleScreen: {
            enabled: false,
            type: `player-iframe`,// player-iframe | redirect (to another page)
            timeout: 30
        },
        playerId: false,
        isKioskDevice: false,
        isMobileLayout: false,
        isSmallLayout: false,
        isTabletLayout: false,
        isMobileOrTabletLayout: false,
        isLargeLayout: false,
        isExtraLargeLayout: false,
        cookieConsent: {
            decided: localStorage.getItem(`cookieConsentDecided`) === `true`,
            analytics: localStorage.getItem(`cookieConsentAnalytics`) === `true`,
            functional: localStorage.getItem(`cookieConsentFunctional`) !== `false`
        }
    },
    getters: {
        cms(state) {
            return state.cms ? state.cms : null;
        },
        currentPage(state) {
            return state.page ? state.page : null;
        },
        currentWebsite(state) {
            return state.website ? state.website : null;
        },
        currentUser(state) {
            return state.user ? state.user : null;
        },
        isAuthenticated(state) {
            return !!state.user;
        },
        displayLanguage(state) {
            if (state.previewLanguage) {
                return state.previewLanguage;
            } else {
                return getLocale();
            }
        },
        env(state) {
            return state.env;
        }
    },
    mutations: {
        showCmsItemInfo(state, itemId) {
            state.cmsItemInfoId = itemId || null;
        },
        /**
         * Force update all components for editor purpose
         * such as applying batch translation or suggestions
         */
        updateCache(state) {
            state.cache = generateGuid();
        },
        setAccessibilityWheelchair(state, wheelchair) {
            state.accessibility.wheelchair = wheelchair;
            localStorage.setItem(`wheelchair`, JSON.stringify(wheelchair));
        },
        showFeedbackModal(state, data = null) {
            state.feedbackModal.visible = true;
            state.feedbackModal.active = true;
            state.feedbackModal.data = data;
        },
        updateDisplayMode(state) {
            if (state.previewMode !== `desktop`) {
                state.displayMode = state.previewMode;
            } else if (state.isKioskDevice) {
                state.displayMode = `kiosk`;
            } else if (state.isMobileLayout) {
                state.displayMode = `mobile`;
            } else if (state.isTabletLayout) {
                state.displayMode = `tablet`;
            } else {
                state.displayMode = `desktop`;
            }
        },
        setZoom(state, zoom) {
            state.zoom = zoom;
        },
        setCookieConsent(state, consent = {}) {
            state.cookieConsent = {
                ...state.cookieConsent,
                ...consent
            };
        },
        toggleBlockHtmlEditor(state, blockId) {
            if (state.blockHtmlEditorEnabled[blockId]) {
                Vue.delete(state.blockHtmlEditorEnabled, blockId);
            } else {
                Vue.set(state.blockHtmlEditorEnabled, blockId, true);
            }
        },
        setCms(state, cms) {
            state.cms = cms;
        },
        setWebsite(state, website) {
            try {
                if (!website) {
                    return;
                }

                // Kiosk payload: every published page comes with the website, prefill the page cache
                // so loadPage() resolves from memory instead of one network request per page
                if (website.allPages) {
                    for (const page of website.allPages) {
                        const permalinks = (page.permalink && typeof page.permalink === `object`) ? Object.values(page.permalink) : [page.permalink];
                        for (const permalink of permalinks) {
                            if (typeof permalink !== `string`) {
                                continue;
                            }
                            // Match CMS.parseRoute() keys: no leading slash, lower case ("/" home becomes "")
                            state.pages[permalink.replace(/^\//, ``).toLowerCase()] = page;
                        }
                        if (page.homePage) {
                            state.pages[``] = page;
                        }
                    }
                    console.log(`Kiosk mode: preloaded ${website.allPages.length} pages for offline navigation`);
                    delete website.allPages;
                }

                website = new CmsWebsite(website);
                state.website = website;
                state.dealerid = state.website.dealerid;

                /* Locale handling */
                const fallback = getFallbackLocale();
                if (!state.website.settings) {
                    state.website.settings = {languages: [fallback]};
                }
                const normalizedLanguages = normalizeLanguageCodes(state.website.settings.languages);
                state.website.settings.languages = normalizedLanguages.length ? normalizedLanguages : [fallback];
                const normalizedFallback = normalizeLocaleCode(state.website.settings.fallbackLanguage) || fallback;
                state.website.settings.fallbackLanguage = normalizedFallback;
                setFallbackLocale(normalizedFallback);

                const storedPreview = normalizeLocaleCode(localStorage.getItem(`previewLanguage`));
                const storedAllowed = storedPreview && state.website.settings.languages.includes(storedPreview) ? storedPreview : null;
                const requestedLocale = normalizeLocaleCode(state.urlLanguage);
                const urlLocale = requestedLocale && state.website.settings.languages.includes(requestedLocale) ? requestedLocale : null;

                // Prefer URL lang, then stored preview language, then fallback language
                const localeToUse = urlLocale || storedAllowed || normalizedFallback;
                console.log(`Setting locale to: `, localeToUse);

                setLocale(localeToUse);
                moment.locale(localeToUse);

                localStorage.setItem(`websiteId`, state.website.id);

                state.website.initWebsite();
                state.website.applyStyle();

                state.cms = new CMS(state.website);
                state.cms.initTypes(website.types);
                state.cms.addItems(website.items);

                if (state.website.map) {
                    this.commit(`mapEditor/setMap`, state.website.map);
                    state.website.map = state.mapEditor.map;
                }

                state.cms.updateDependencies();
                state.applicationReady = true;

                // for debug only
                window.cmsWebsite = website;
                window.cms = state.cms;
            } catch (e) {
                console.error(e);
                swal(`Network Error`, `Website is not valid`, `error`);
            }
        },
        setPages(state, pages) {
            try {
                state.website.pages = pages;
            } catch (e) {
                console.error(e);
                swal(`Network Error`, `Couldn't load pages`, `error`);
            }
        },
        setPage(state, data) {
            if (!data?.page) {
                state.page = null;
                window.cmsPage = null;
                return;
            }
            try {
                let page = new CmsPage(data.page, state.cms, data.route);
                if (!page.mediaProxy.visible && !state.editMode) {
                    if (page.fallbackLink) {
                        return router.push(page.fallbackLink.to || `/`);
                    }
                }
                state.page = page;
                window.cmsPage = state.page;
            } catch (e) {
                console.error(e);
                swal(`Network Error`, `Page is not valid`, `error`);
            }
        },
        setIdleScreen(state, settings) {
            state.idleScreen.enabled = settings.enabled;
            state.idleScreen.timeout = settings.timeout;
            state.idleScreen.type = settings.type;
        },
        checkWebsiteError(state, error) {
            console.error(error);
            state.website = null;
            window.lastError = error;
            if (error?.response?.data?.error === `PASSWORD_REQUIRED`) {
                state.applicationReady = true;
                router.push(`/password-required`);
            } else {
                swal(`Network Error`, `Couldn't load website`, `error`);
            }
        },
        startLogin(state) {
            state.loginStatus = `loading`
        },
        authSuccess(state, user) {
            state.loginStatus = `success`;
            state.user = user
        },
        notConnected(state) {
            state.loginStatus = `not_connected`;
        },
        authError(state) {
            state.loginStatus = `error`
        },
        loggedOut(state) {
            state.loginStatus = `logged_out`;
            state.user = null
        }
    },
    actions: {
        async loadWebsite({state, commit}, data = {id: null, domain: null}) {
            try {
                if (state.applicationReady) {
                    return;
                }
                let url = `/api/public/website`;
                if (state.user && data.id) {
                    url = `/api/website/${data.id}`;
                } else {
                    if (data.domain) {
                        url += `/${data.domain}`;
                    } else {
                        url += `/${window.location.hostname}`;

                        if (!data.id && localStorage.getItem(`websiteId`)) {
                            data.id = localStorage.getItem(`websiteId`);
                        }

                        // _validDomain is added by SSR part when the domain is matching a website
                        if (data.id && !window._validDomain) {
                            url += `/${data.id}`;
                        }
                    }

                    const queryParams = new URLSearchParams();
                    if (localStorage.getItem(`password`)) {
                        queryParams.set(`password`, localStorage.getItem(`password`));
                    }
                    if (isKioskRequest(state)) {
                        // Kiosks receive every published page in the website payload for offline navigation
                        queryParams.set(`kiosk`, `1`);
                    }
                    if (queryParams.toString()) {
                        url += `?${queryParams.toString()}`;
                    }
                }

                const kiosk = isKioskRequest(state);
                if (kiosk && navigator.storage?.persist) {
                    // Best-effort storage is eligible for eviction under disk pressure
                    navigator.storage.persist().catch(() => {});
                }

                let website = null;
                try {
                    // Note: NetworkV2 returns a broken error [Object object] instead of a true object
                    const response = await Network.get(url);
                    website = response.data;
                } catch (e) {
                    if (!kiosk) {
                        throw e;
                    }
                    await logKioskCacheState(url);
                    website = await loadKioskWebsiteCopy();
                    if (!website) {
                        throw e;
                    }
                    console.warn(`Website loaded from the kiosk offline copy`);
                    commit(`setWebsite`, website);
                    return;
                }

                // Serialize before setWebsite: the mutation strips allPages from the payload
                const serializedWebsite = kiosk ? JSON.stringify(website) : null;
                commit(`setWebsite`, website);
                if (serializedWebsite) {
                    saveKioskWebsite(serializedWebsite);
                }
            } catch (e) {
                commit(`checkWebsiteError`, e);
            }
        },
        async refreshWebsite({state, commit}) {
            try {
                let url = `/api/public/website/${window.location.hostname}/${state.website.id}`;

                const queryParams = new URLSearchParams();
                if (localStorage.getItem(`password`)) {
                    queryParams.set(`password`, localStorage.getItem(`password`));
                }
                if (isKioskRequest(state)) {
                    queryParams.set(`kiosk`, `1`);
                }
                if (queryParams.toString()) {
                    url += `?${queryParams.toString()}`;
                }

                const response = await Network.get(url);
                const serializedWebsite = isKioskRequest(state) ? JSON.stringify(response.data) : null;
                commit(`setWebsite`, response.data);
                if (serializedWebsite) {
                    saveKioskWebsite(serializedWebsite);
                }
            } catch (e) {
                commit(`checkWebsiteError`, e);
            }
        },
        // async reloadPages({state, commit, dispatch}, websiteId) {
        //     try {
        //         const resp = await Network.get(`/api/pages/website/${websiteId}`);
        //         commit(`setPages`, resp.data);
        //     } catch (e) {
        //         console.error(e);
        //         swal(`Network Error`, `Coulnd't load pages`, `error`);
        //     }
        //     state.applicationLoading = false;
        // },
        async loadPage({state, currentWebsite, commit, dispatch}, route) {
            state.applicationLoading = true;
            try {
                if (!state.applicationReady) {
                    await dispatch(`loadWebsite`, {domain: route?.query.domain});
                }
                if (!state.website) {
                    // loadWebsite already reported the error, nothing can be rendered without a website
                    state.applicationLoading = false;
                    return;
                }
                let routeParams = CMS.parseRoute(route.fullPath);
                for (let redirect of state.website.routing?.redirects || []) {
                    if (!redirect.exactMatch && `/${routeParams.permalink}`.indexOf(redirect.from) === 0 || redirect.from === `/${routeParams.permalink}`) {
                        if (redirect.to.indexOf(`https`) === 0) {
                            return window.location.href = redirect.to;
                        } else {
                            return router.push(redirect.to);
                        }
                    }
                }
                if (state.pages[routeParams.permalink]) {
                    commit(`setPage`, {page: state.pages[routeParams.permalink], route});
                } else {
                    const response = await Network.get(`/api/public/pages/${state.website.dealerid}/permalink/${routeParams.permalink}`)
                    if (response.data) {
                        state.pages[routeParams.permalink] = response.data;
                    }
                    commit(`setPage`, {page: response.data, route});
                }
            } catch (e) {
                commit(`setPage`, null);
                if (e?.response?.status === 404) {
                    swal({
                        title: tr(`Page unavailable|Page indisponible`),
                        text: tr(`This page is not available. Please choose another result.|Cette page n'est pas disponible. Veuillez choisir un autre résultat.`),
                        icon: `info`
                    });
                } else {
                    console.error(e);
                    swal(`Network Error`, `Couldn't load page`, `error`);
                }
            } finally {
                state.applicationLoading = false;
            }
        },
        async getCurrentUser({commit, dispatch}) {
            commit(`startLogin`);
            let token = null;

            const currentURL = new URL(document.URL);
            const currentURLParams = new URLSearchParams(currentURL.search);

            if (currentURLParams.has(`token`)) {
                token = currentURLParams.get(`token`);
            }

            if (!token) {
                token = localStorage.getItem(`token`);
            }

            if (!token) {
                commit(`notConnected`);
                return false;
            }

            Network.setToken(token);
            NetworkV2.setToken(token);
            try {
                const response = await Network.get(`/users/current-user`, null, {system_api: true})
                commit(`authSuccess`, response.data);
            } catch (e) {
                console.error(e);
                dispatch(`logout`, {forceLogout: true});
            }
        },
        logout({commit}, payload = {}) {
            commit(`loggedOut`);
            localStorage.removeItem(`token`);
            Network.removeToken();
            NetworkV2.removeToken();

            const redirectURL = new URL(window.env.VUE_APP_URLS__LOGIN);
            redirectURL.searchParams.set(`redirect_url`, window.location.href);
            if (payload.forceLogout) {
                redirectURL.searchParams.set(`logout`, `1`);
            }

            window.location.href = redirectURL.toString();
        }
    },
    modules: {
        googleFonts: GoogleFontStore,
        mapEditor: MapStore,
        clipboard: ClipboardStore,
        assistant: AssistantStore,
        comparison: TextCompareStore
    }
});
